import React, { memo } from "react";

import { classNames } from "helpers";
import { ShowcaseBackdrop, ShowcaseStage } from "ui/components/Showcase";
import Logo from "ui/modules/public/components/Logo/Logo";

import { DRIVER_DOCUMENT_STEPS } from "../../consts";
import { DRIVER_DOCUMENT_INTRO_DESCRIPTION, DRIVER_PERSON_INTRO_TEXT } from "./consts";
import DriverDocumentScene from "./scenes/DriverDocumentScene";
import DriverPersonScene from "./scenes/DriverPersonScene";
import { DriverIntroProps } from "./types";

/**
 * Lijevi panel unosa vozača: prvi korak prikazuje vozača, a svaki sljedeći jednu ispravu s
 * rokom važenja. Isti obrazac kao intro čarobnjaka vozila.
 */
const DriverIntro: React.FC<DriverIntroProps> = ({ formValues, source, stepCurrent, stepCount }) => {
  const documentStep = stepCurrent > 0 ? DRIVER_DOCUMENT_STEPS[stepCurrent - 1] : undefined;
  const title = documentStep ? documentStep.title : DRIVER_PERSON_INTRO_TEXT.title;
  const description = documentStep ? DRIVER_DOCUMENT_INTRO_DESCRIPTION : DRIVER_PERSON_INTRO_TEXT.description;

  return (
    <div className="relative flex flex-col w-full h-full bg-blue-700 overflow-hidden">
      <ShowcaseBackdrop />
      <Logo variant="light" animated className="absolute top-8 left-8 z-10" />

      <div className="relative flex flex-1 min-h-0 items-center justify-center select-none pointer-events-none" aria-hidden="true">
        {/* `key`: novi korak iznova montira scenu, pa kartice ponovno ulete. */}
        <ShowcaseStage key={stepCurrent}>
          {documentStep ? (
            <DriverDocumentScene formValues={formValues} step={documentStep} />
          ) : (
            <DriverPersonScene formValues={formValues} source={source} />
          )}
        </ShowcaseStage>
      </div>

      <div className="relative px-8 pb-12 text-center">
        <div className="text-xs font-semibold uppercase tracking-widest text-blue-200">
          Korak {stepCurrent + 1} od {stepCount}
        </div>
        <div key={stepCurrent} className="motion-safe:animate-showcaseFadeIn">
          <div className="mt-2 text-3xl font-bold leading-tight text-white">{title}</div>
          {/* Visina dva retka: opisi su različite duljine, a scena iznad ne smije skakati. */}
          <p className="mt-3 mx-auto max-w-md min-h-[3rem] text-base text-blue-100">{description}</p>
        </div>
        <div className="flex items-center justify-center gap-2 mt-6">
          {Array.from({ length: stepCount }, (_, index) => (
            <span
              key={index}
              className={classNames(
                "h-1.5 rounded-full transition-all duration-300",
                index === stepCurrent ? "w-6 bg-amber-400" : "w-1.5 bg-white/40"
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default memo(DriverIntro);
